// 🔧 promotion_categories.js
// ✅ Purpose: Central content category registry for the LGV Promotion Engine
// ✅ Features:
// - Defines every content category used by the Promotion Engine
// - Organizes categories into logical groups
// - Supports AI content generation
// - Supports rotation engine balancing
// - Supports dashboard analytics
// - Designed for future Event Promotion Add-on
// - Fully block-commented for clarity and future devs

/**
 * ============================================================================
 * CATEGORY GROUPS
 * ============================================================================
 *
 * These groups help organize the admin UI while also giving the AI
 * additional context about the purpose of each category.
 *
 * Groups are NOT stored on queue records.
 * Queue records store only the stable category key.
 */

export const PROMOTION_CATEGORY_GROUPS = {
  PRODUCT: "product",
  EDUCATION: "education",
  TRUST: "trust",
  INDUSTRIES: "industries",
  COMMUNITY: "community",
  COMPANY: "company",
  EVENTS: "events",          // Future MVP2
  SPONSORS: "sponsors",      // Future MVP2
  VENDORS: "vendors",        // Future MVP2
  VOLUNTEERS: "volunteers",  // Future MVP2
};

/**
 * ============================================================================
 * STABLE CATEGORY KEYS
 * ============================================================================
 *
 * IMPORTANT:
 * These values become Firestore data.
 * Never rename existing keys.
 */

export const PROMOTION_CATEGORY_KEYS = {
  AUCTION_ITEM: "auction_item",

  // ---------- PRODUCT ----------

  FEATURES: "features",
  ENGINES: "engines",
  VOTING_TYPES: "voting_types",
  RELEASES: "releases",

  // ---------- EDUCATION ----------

  HOW_TO: "how_to",
  FAQ: "faq",
  TIPS: "tips",
  BEST_PRACTICES: "best_practices",

  // ---------- TRUST ----------

  SECURITY: "security",
  INTEGRITY: "integrity",
  VALIDATION: "validation",
  TRUST: "trust",

  // ---------- INDUSTRIES ----------

  CAR_SHOWS: "car_shows",
  ART_SHOWS: "art_shows",
  PET_SHOWS: "pet_shows",
  SCHOOLS: "schools",
  FESTIVALS: "festivals",
  NONPROFITS: "nonprofits",

  // ---------- COMMUNITY ----------

  CUSTOMER_STORIES: "customer_stories",
  TESTIMONIALS: "testimonials",
  BEHIND_THE_SCENES: "behind_the_scenes",

  // ---------- COMPANY ----------

  COMPANY_NEWS: "company_news",
  MILESTONES: "milestones",

  // ---------- FUTURE EVENT CONTENT ----------

  EVENT_PROMOTION: "event_promotion",
  SPONSOR_SPOTLIGHT: "sponsor_spotlight",
  VENDOR_SPOTLIGHT: "vendor_spotlight",
  VOLUNTEER_SPOTLIGHT: "volunteer_spotlight",
  WINNER_SPOTLIGHT: "winner_spotlight",
};

/**
 * ============================================================================
 * MASTER CATEGORY LIST
 * ============================================================================
 */

export const PROMOTION_CATEGORIES = [
  { key: PROMOTION_CATEGORY_KEYS.AUCTION_ITEM, group: PROMOTION_CATEGORY_GROUPS.EVENTS, label: "Auction Item", description: "Promote an event Auction item." },

  // -------------------------------------------------------------------------
  // PRODUCT
  // -------------------------------------------------------------------------

  {
    key: PROMOTION_CATEGORY_KEYS.FEATURES,
    group: PROMOTION_CATEGORY_GROUPS.PRODUCT,
    label: "Features",
    futureEventSupport: true,
    description:
      "Highlights specific capabilities available in LGV.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.ENGINES,
    group: PROMOTION_CATEGORY_GROUPS.PRODUCT,
    label: "LGV Engines",
    futureEventSupport: false,
    description:
      "Explains the purpose and benefits of individual LGV Engines.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.VOTING_TYPES,
    group: PROMOTION_CATEGORY_GROUPS.PRODUCT,
    label: "Voting Types",
    futureEventSupport: true,
    description:
      "Educational content about available voting methods.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.RELEASES,
    group: PROMOTION_CATEGORY_GROUPS.PRODUCT,
    label: "Product Updates",
    futureEventSupport: false,
    description:
      "Announcements about new functionality and improvements.",
  },

  // -------------------------------------------------------------------------
  // EDUCATION
  // -------------------------------------------------------------------------

  {
    key: PROMOTION_CATEGORY_KEYS.HOW_TO,
    group: PROMOTION_CATEGORY_GROUPS.EDUCATION,
    label: "How-To",
    futureEventSupport: true,
    description:
      "Educational walkthroughs and setup guides.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.FAQ,
    group: PROMOTION_CATEGORY_GROUPS.EDUCATION,
    label: "Frequently Asked Questions",
    futureEventSupport: true,
    description:
      "Answers common organizer questions.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.TIPS,
    group: PROMOTION_CATEGORY_GROUPS.EDUCATION,
    label: "Tips",
    futureEventSupport: true,
    description:
      "Helpful recommendations and quick advice.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.BEST_PRACTICES,
    group: PROMOTION_CATEGORY_GROUPS.EDUCATION,
    label: "Best Practices",
    futureEventSupport: true,
    description:
      "Professional recommendations learned from successful events.",
  },

  // -------------------------------------------------------------------------
  // TRUST
  // -------------------------------------------------------------------------

  {
    key: PROMOTION_CATEGORY_KEYS.SECURITY,
    group: PROMOTION_CATEGORY_GROUPS.TRUST,
    label: "Security",
    futureEventSupport: true,
    description:
      "Protecting organizers and participant information.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.INTEGRITY,
    group: PROMOTION_CATEGORY_GROUPS.TRUST,
    label: "Integrity",
    futureEventSupport: true,
    description:
      "Transparent, auditable event processes.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.VALIDATION,
    group: PROMOTION_CATEGORY_GROUPS.TRUST,
    label: "Validation",
    futureEventSupport: true,
    description:
      "Testing, verification, and confidence-building content.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.TRUST,
    group: PROMOTION_CATEGORY_GROUPS.TRUST,
    label: "Trust",
    futureEventSupport: true,
    description:
      "General messaging around confidence and professionalism.",
  },

  // -------------------------------------------------------------------------
  // INDUSTRIES
  // -------------------------------------------------------------------------

  {
    key: PROMOTION_CATEGORY_KEYS.CAR_SHOWS,
    group: PROMOTION_CATEGORY_GROUPS.INDUSTRIES,
    label: "Car Shows",
    futureEventSupport: true,
    description:
      "Examples and ideas specific to car shows.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.ART_SHOWS,
    group: PROMOTION_CATEGORY_GROUPS.INDUSTRIES,
    label: "Art Shows",
    futureEventSupport: true,
    description:
      "Examples and ideas specific to art events.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.PET_SHOWS,
    group: PROMOTION_CATEGORY_GROUPS.INDUSTRIES,
    label: "Pet Shows",
    futureEventSupport: true,
    description:
      "Examples and ideas specific to pet events.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.SCHOOLS,
    group: PROMOTION_CATEGORY_GROUPS.INDUSTRIES,
    label: "Schools",
    futureEventSupport: true,
    description:
      "Content related to student organizations and school elections.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.FESTIVALS,
    group: PROMOTION_CATEGORY_GROUPS.INDUSTRIES,
    label: "Festivals",
    futureEventSupport: true,
    description:
      "Festival and fair promotion ideas.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.NONPROFITS,
    group: PROMOTION_CATEGORY_GROUPS.INDUSTRIES,
    label: "Nonprofits",
    futureEventSupport: true,
    description:
      "Fundraising and nonprofit engagement content.",
  },

  // -------------------------------------------------------------------------
  // COMMUNITY
  // -------------------------------------------------------------------------

  {
    key: PROMOTION_CATEGORY_KEYS.CUSTOMER_STORIES,
    group: PROMOTION_CATEGORY_GROUPS.COMMUNITY,
    label: "Customer Stories",
    futureEventSupport: true,
    description:
      "Success stories from organizers.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.TESTIMONIALS,
    group: PROMOTION_CATEGORY_GROUPS.COMMUNITY,
    label: "Testimonials",
    futureEventSupport: true,
    description:
      "Customer quotes and experiences.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.BEHIND_THE_SCENES,
    group: PROMOTION_CATEGORY_GROUPS.COMMUNITY,
    label: "Behind the Scenes",
    futureEventSupport: true,
    description:
      "People and work behind great events.",
  },

  // -------------------------------------------------------------------------
  // COMPANY
  // -------------------------------------------------------------------------

  {
    key: PROMOTION_CATEGORY_KEYS.COMPANY_NEWS,
    group: PROMOTION_CATEGORY_GROUPS.COMPANY,
    label: "Company News",
    futureEventSupport: false,
    description:
      "Updates about Let's Go Vote.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.MILESTONES,
    group: PROMOTION_CATEGORY_GROUPS.COMPANY,
    label: "Milestones",
    futureEventSupport:false,
    description:
      "Major accomplishments and company growth.",
  },

  // -------------------------------------------------------------------------
  // FUTURE EVENT PROMOTION
  // -------------------------------------------------------------------------

  {
    key: PROMOTION_CATEGORY_KEYS.EVENT_PROMOTION,
    group: PROMOTION_CATEGORY_GROUPS.EVENTS,
    label: "Event Promotion",
    futureEventSupport: true,
    description:
      "General event promotion content.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.SPONSOR_SPOTLIGHT,
    group: PROMOTION_CATEGORY_GROUPS.SPONSORS,
    label: "Sponsor Spotlight",
    futureEventSupport: true,
    description:
      "Highlight event sponsors.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.VENDOR_SPOTLIGHT,
    group: PROMOTION_CATEGORY_GROUPS.VENDORS,
    label: "Vendor Spotlight",
    futureEventSupport: true,
    description:
      "Highlight participating vendors.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.VOLUNTEER_SPOTLIGHT,
    group: PROMOTION_CATEGORY_GROUPS.VOLUNTEERS,
    label: "Volunteer Spotlight",
    futureEventSupport: true,
    description:
      "Recognize volunteers and recruit helpers.",
  },

  {
    key: PROMOTION_CATEGORY_KEYS.WINNER_SPOTLIGHT,
    group: PROMOTION_CATEGORY_GROUPS.EVENTS,
    label: "Winner Spotlight",
    futureEventSupport: true,
    description:
      "Celebrate event winners and achievements.",
  },
];

/**
 * Returns a category object by key.
 */
export const getPromotionCategoryByKey = (key) =>
  PROMOTION_CATEGORIES.find((category) => category.key === key) || null;

/**
 * Returns a readable label from a key.
 */
export const getPromotionCategoryLabel = (key) =>
  getPromotionCategoryByKey(key)?.label || "Unknown Category";

/**
 * Returns categories belonging to a specific group.
 */
export const getPromotionCategoriesByGroup = (group) =>
  PROMOTION_CATEGORIES.filter((category) => category.group === group);
